import { UseMutationOptions, useMutation, useQueryClient } from 'react-query';
import { getUser } from '@frontend/auth-helpers';
import { HTTPBoundSchemaMethod, SchemaIO } from '@frontend/schema';
import { SchemaAuthService } from './api';
import { queryKeys } from './queries';

type SchemaMutationOptions<T extends HTTPBoundSchemaMethod<any>> = UseMutationOptions<
  SchemaIO<T>['output'],
  unknown,
  SchemaIO<T>['input'],
  unknown
>;

export const MAX_RETRY_COUNT = 2;
const defaultRetryConfig = {
  retry: (failureCount: number, error: any) => {
    // Retry on server errors (5xx)
    if (error?.status && error.status >= 500) {
      return failureCount < MAX_RETRY_COUNT;
    }
    // Retry on network errors (no status = network failure)
    if (!error?.status) {
      return failureCount < MAX_RETRY_COUNT;
    }
    return false;
  },
  // Exponential backoff delay
  // 1st try: 250ms, 2nd try: 500ms, 3rd try: 1000ms
  retryDelay: (attempt: number) => Math.pow(2, attempt - 1) * 250,
};

export const useAddContact = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['AddContact']> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'AddContact',
    mutationFn: SchemaAuthService.AddContact,
    ...defaultRetryConfig,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useEnableMFA = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['EnableMFA']> = {}) =>
  useMutation({
    mutationKey: 'EnableMFA',
    mutationFn: SchemaAuthService.EnableMFA,
    ...defaultRetryConfig,
    ...opts,
  });

export const useDisableMFA = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['DisableMFA']> = {}) =>
  useMutation({
    mutationKey: 'DisableMFA',
    mutationFn: SchemaAuthService.DisableMFA,
    ...defaultRetryConfig,
    ...opts,
  });

export const useValidateContact = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['ValidateContact']> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'ValidateContact',
    mutationFn: SchemaAuthService.ValidateContact,
    ...defaultRetryConfig,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useDeleteContact = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['DeleteContact']> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'DeleteContact',
    mutationFn: SchemaAuthService.DeleteContact,
    ...defaultRetryConfig,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useMakeContactPrimary = (
  opts: SchemaMutationOptions<(typeof SchemaAuthService)['MakeContactPrimary']> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: 'MakeContactPrimary',
    mutationFn: SchemaAuthService.MakeContactPrimary,
    ...defaultRetryConfig,
    ...opts,
    onSuccess(...args) {
      queryClient.invalidateQueries(queryKeys.userDetails(getUser()?.userID ?? ''));
      opts?.onSuccess?.apply(this, args);
    },
  });
};

export const useResendContactValidation = (
  opts: SchemaMutationOptions<(typeof SchemaAuthService)['ResendContactValidation']> = {}
) =>
  useMutation({
    mutationKey: 'ResendContactValidation',
    mutationFn: SchemaAuthService.ResendContactValidation,
    ...defaultRetryConfig,
    ...opts,
  });

export const useCreateImpression = (opts: SchemaMutationOptions<(typeof SchemaAuthService)['CreateImpression']> = {}) =>
  useMutation({
    mutationKey: 'CreateImpression',
    mutationFn: SchemaAuthService.CreateImpression,
    ...defaultRetryConfig,
    ...opts,
  });
